import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Trip } from '../../../../../types/cms.types';
import './CpnDetailTrip.css';

interface CpnDetailTripProps {
    visible: boolean;
    onHide: () => void;
    trip: Trip | null;
}

export const CpnDetailTrip: React.FC<CpnDetailTripProps> = ({
    visible,
    onHide,
    trip
}) => {
    if (!trip) return null;

    // Status severity mapping
    const getStatusSeverity = (status: string): "success" | "info" | "warning" | "danger" => {
        switch (status) {
            case 'completed': return 'success';
            case 'in_progress':
            case 'going_pickup':
            case 'accepted': return 'info';
            case 'requested': return 'warning';
            case 'cancelled_by_driver':
            case 'cancelled_by_user': return 'danger';
            default: return 'info';
        }
    };

    // Status label mapping
    const getStatusLabel = (status: string): string => {
        const statusLabels: { [key: string]: string } = {
            requested: 'Đã yêu cầu',
            accepted: 'Đã nhận',
            going_pickup: 'Đang đến điểm đón',
            in_progress: 'Đang di chuyển',
            completed: 'Hoàn thành',
            cancelled_by_driver: 'Tài xế hủy',
            cancelled_by_user: 'Khách hàng hủy'
        };
        return statusLabels[status] || status;
    };

    // Format currency
    const formatCurrency = (amount: string | number) => {
        return parseFloat(amount?.toString() || '0').toLocaleString('vi-VN') + 'đ';
    };

    // Format date time
    const formatDateTime = (dateString?: string) => {
        if (!dateString) return 'Chưa có thông tin';
        return new Date(dateString).toLocaleString('vi-VN');
    };

    // Header template
    const headerTemplate = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <i className="pi pi-eye" style={{ fontSize: '1.2rem', color: '#3b82f6' }}></i>
            <span style={{ fontSize: '1.125rem', fontWeight: '600' }}>Chi tiết chuyến đi</span>
            <Tag
                value={getStatusLabel(trip.status)}
                severity={getStatusSeverity(trip.status)}
                style={{ marginLeft: 'auto' }}
            />
        </div>
    );

    // Footer template
    const footerTemplate = (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
            <Button
                label="Đóng"
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-outlined"
                size="small"
            />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            style={{ width: '90vw', maxWidth: '70vw' }}
            header={headerTemplate}
            footer={footerTemplate}
            onHide={onHide}
            // maximizable
            modal
            className="trip-detail-dialog "
        >
            <div style={{ padding: '14px' }}>
                {/* Trip ID và thông tin cơ bản */}
                <div className="info-card" style={{
                    background: '#f8fafc',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid #e2e8f0'
                }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                        <div>
                            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                Mã chuyến đi
                            </label>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.875rem', color: '#1e293b' }}>
                                {trip.id}
                            </span>
                        </div>
                        <div>
                            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                Loại xe
                            </label>
                            <Tag
                                value={trip.vehicle_type === 'car' ? 'Ô tô' : 'Xe máy'}
                                severity={trip.vehicle_type === 'car' ? 'info' : 'success'}
                                icon={trip.vehicle_type === 'car' ? 'pi pi-car' : 'pi pi-truck'}
                            />
                        </div>
                        {trip.seats && (
                            <div>
                                <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                    Số ghế
                                </label>
                                <span style={{ fontSize: '0.875rem', color: '#1e293b' }}>
                                    {trip.seats} chỗ ngồi
                                </span>
                            </div>
                        )}
                        {trip.kilometers_distance && (
                            <div>
                                <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                    Khoảng cách
                                </label>
                                <span style={{ fontSize: '0.875rem', color: '#1e293b' }}>
                                    {trip.kilometers_distance} km
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Thông tin khách hàng và tài xế */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    {/* Khách hàng */}
                    <div className="info-card" style={{
                        background: '#fefefe',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <h4 style={{
                            margin: '0 0 12px 0',
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <i className="pi pi-user" style={{ color: '#3b82f6' }}></i>
                            Thông tin khách hàng
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: '500', color: '#64748b' }}>Họ tên:</label>
                                <div style={{ fontSize: '0.875rem', color: '#1e293b', fontWeight: '500' }}>
                                    {trip.customer?.full_name || trip.customer_name || 'Chưa có thông tin'}
                                </div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: '500', color: '#64748b' }}>Số điện thoại:</label>
                                <div style={{ fontSize: '0.875rem', color: '#1e293b' }}>
                                    {trip.customer?.phone_number || trip.customer_phone_number || 'Chưa có thông tin'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tài xế */}
                    <div className="info-card" style={{
                        background: '#fefefe',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <h4 style={{
                            margin: '0 0 12px 0',
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <i className="pi pi-users" style={{ color: '#10b981' }}></i>
                            Thông tin tài xế
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: '500', color: '#64748b' }}>Họ tên:</label>
                                <div style={{ fontSize: '0.875rem', color: '#1e293b', fontWeight: '500' }}>
                                    {trip.driver?.full_name || trip.driver_name || 'Chưa phân công'}
                                </div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: '500', color: '#64748b' }}>Số điện thoại:</label>
                                <div style={{ fontSize: '0.875rem', color: '#1e293b' }}>
                                    {trip.driver?.phone_number || trip.driver_phone_number || 'Chưa có thông tin'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Thông tin tuyến đường */}
                <div style={{
                    background: '#fefefe',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    marginBottom: '20px'
                }}>
                    <h4 style={{
                        margin: '0 0 16px 0',
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <i className="pi pi-map" style={{ color: '#8b5cf6' }}></i>
                        Tuyến đường
                    </h4>

                    {/* Điểm đón */}
                    <div style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <i className="pi pi-circle-fill" style={{ color: '#10b981', fontSize: '0.75rem', marginTop: '4px' }}></i>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '0.75rem', fontWeight: '500', color: '#64748b' }}>Điểm đón:</label>
                                <div style={{ fontSize: '0.875rem', color: '#1e293b', marginTop: '2px' }}>
                                    {trip.pickup_address || 'Chưa có thông tin'}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                                    Tọa độ: {trip.pickup_location_lat}, {trip.pickup_location_long}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Điểm dừng 1 */}
                    {trip.address_stop_one && (
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                <i className="pi pi-circle" style={{ color: '#f59e0b', fontSize: '0.75rem', marginTop: '4px' }}></i>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '0.75rem', fontWeight: '500', color: '#64748b' }}>Điểm dừng 1:</label>
                                    <div style={{ fontSize: '0.875rem', color: '#1e293b', marginTop: '2px' }}>
                                        {trip.address_stop_one}
                                    </div>
                                    {trip.stop_one_location_lat && trip.stop_one_location_long && (
                                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                                            Tọa độ: {trip.stop_one_location_lat}, {trip.stop_one_location_long}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Điểm dừng 2 */}
                    {trip.address_stop_two && (
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                <i className="pi pi-circle" style={{ color: '#f59e0b', fontSize: '0.75rem', marginTop: '4px' }}></i>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '0.75rem', fontWeight: '500', color: '#64748b' }}>Điểm dừng 2:</label>
                                    <div style={{ fontSize: '0.875rem', color: '#1e293b', marginTop: '2px' }}>
                                        {trip.address_stop_two}
                                    </div>
                                    {trip.stop_two_location_lat && trip.stop_two_location_long && (
                                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                                            Tọa độ: {trip.stop_two_location_lat}, {trip.stop_two_location_long}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Điểm đến */}
                    <div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <i className="pi pi-map-marker" style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px' }}></i>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '0.75rem', fontWeight: '500', color: '#64748b' }}>Điểm đến:</label>
                                <div style={{ fontSize: '0.875rem', color: '#1e293b', marginTop: '2px' }}>
                                    {trip.dropoff_address || 'Chưa có thông tin'}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                                    Tọa độ: {trip.dropoff_location_lat}, {trip.dropoff_location_long}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Thông tin thời gian */}
                <div style={{
                    background: '#fefefe',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    marginBottom: '20px'
                }}>
                    <h4 style={{
                        margin: '0 0 16px 0',
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <i className="pi pi-clock" style={{ color: '#6366f1' }}></i>
                        Thông tin thời gian
                    </h4>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                        <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: '500', color: '#64748b' }}>Thời gian tạo:</label>
                            <div style={{ fontSize: '0.875rem', color: '#1e293b', marginTop: '2px' }}>
                                {formatDateTime(trip.created_at)}
                            </div>
                        </div>

                        {trip.start_time && (
                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: '500', color: '#64748b' }}>Thời gian bắt đầu:</label>
                                <div style={{ fontSize: '0.875rem', color: '#1e293b', marginTop: '2px' }}>
                                    {formatDateTime(trip.start_time)}
                                </div>
                            </div>
                        )}

                        {trip.end_time && (
                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: '500', color: '#64748b' }}>Thời gian kết thúc:</label>
                                <div style={{ fontSize: '0.875rem', color: '#1e293b', marginTop: '2px' }}>
                                    {formatDateTime(trip.end_time)}
                                </div>
                            </div>
                        )}

                        <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: '500', color: '#64748b' }}>Cập nhật lần cuối:</label>
                            <div style={{ fontSize: '0.875rem', color: '#1e293b', marginTop: '2px' }}>
                                {formatDateTime(trip.updated_at)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Thông tin giá cả */}
                <div style={{
                    background: '#fefefe',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    marginBottom: '20px',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                }}>
                    <h4 style={{
                        margin: '0 0 20px 0',
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <i className="pi pi-money-bill" style={{ color: '#059669', fontSize: '1.2rem' }}></i>
                        Thông tin giá cả
                    </h4>

                    {/* Chi tiết giá - Grid layout */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '16px',
                        marginBottom: '20px'
                    }}>
                        {/* Giá cơ bản */}
                        <div style={{
                            padding: '12px',
                            background: '#f8fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <label style={{
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                color: '#64748b',
                                display: 'block',
                                marginBottom: '4px'
                            }}>
                                Giá cơ bản:
                            </label>
                            <div style={{
                                fontSize: '0.9rem',
                                color: '#1e293b',
                                fontWeight: '600'
                            }}>
                                {trip.price_base ? formatCurrency(trip.price_base) : '0đ'}
                            </div>
                        </div>

                        {/* Phí dịch vụ */}
                        <div style={{
                            padding: '12px',
                            background: '#f8fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <label style={{
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                color: '#64748b',
                                display: 'block',
                                marginBottom: '4px'
                            }}>
                                Phí dịch vụ:
                            </label>
                            <div style={{
                                fontSize: '0.9rem',
                                color: '#1e293b',
                                fontWeight: '600'
                            }}>
                                {trip.price_service ? formatCurrency(trip.price_service) : '0đ'}
                            </div>
                        </div>

                        {/* Giá khuyến mãi */}
                        <div style={{
                            padding: '12px',
                            background: trip.price_promotion ? '#fef2f2' : '#f8fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <label style={{
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                color: '#64748b',
                                display: 'block',
                                marginBottom: '4px'
                            }}>
                                Giá khuyến mãi:
                            </label>
                            <div style={{
                                fontSize: '0.9rem',
                                color: trip.price_promotion ? '#dc2626' : '#64748b',
                                fontWeight: '600'
                            }}>
                                {trip.price_promotion ? `-${formatCurrency(trip.price_promotion)}` : '0đ'}
                            </div>
                        </div>
                    </div>

                    {/* Đường kẻ phân cách */}
                    <div style={{
                        height: '1px',
                        background: 'linear-gradient(to right, transparent, #e2e8f0, transparent)',
                        marginBottom: '16px'
                    }}></div>

                    {/* Tổng tiền - Nổi bật */}
                    <div style={{
                        padding: '16px 20px',
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '8px'
                        }}>
                            <label style={{
                                fontSize: '1rem',
                                fontWeight: '700',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <i className="pi pi-credit-card" style={{ fontSize: '1.1rem' }}></i>
                                TỔNG TIỀN:
                            </label>
                            <div style={{
                                fontSize: '1.5rem',
                                color: 'white',
                                fontWeight: '800',
                                textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                letterSpacing: '0.5px'
                            }}>
                                {formatCurrency(trip.price || 0)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Lý do hủy (nếu có) */}
                {trip.reason_cancel_trip && (
                    <div style={{
                        background: '#fef2f2',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #fecaca'
                    }}>
                        <h4 style={{
                            margin: '0 0 8px 0',
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#dc2626',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <i className="pi pi-exclamation-triangle" style={{ color: '#dc2626' }}></i>
                            Lý do hủy chuyến
                        </h4>
                        <div style={{ fontSize: '0.875rem', color: '#991b1b' }}>
                            {trip.reason_cancel_trip}
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
};
